<template>
  <v-container class="create-user-page">
    <v-row justify="center">
      <v-col cols="12" lg="8" xl="6">
        <v-card class="elevation-2">
          <v-app-bar color="primary" dark class="elevation-0">
            <v-btn @click="$router.push({ name: 'Home' })" class="d-block me-2" elevation="0" fab small color="primary">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <v-toolbar-title>
              {{ translate($store.getters.content.create_user) }}
            </v-toolbar-title>
          </v-app-bar>
          <v-card-text class="px-8 pb-8">
            <v-form v-model="formValid" ref="createUserForm" @submit.prevent="createUser()">
              <v-alert type="error" v-if="formError" dense dismissible class="mt-8">{{
                translate($store.getters.content.fill_required_fields)
              }}</v-alert>

              <!-- name-->
              <v-text-field
                v-model="formData.firstName"
                :rules="[(v) => !!v || translate($store.getters.content.required)]"
                class="required ma-3"
                :label="translate($store.getters.content.first_name)"
                required
                hide-details
              ></v-text-field>

              <!-- last name -->
              <v-text-field
                v-model="formData.lastName"
                :rules="[(v) => !!v || translate($store.getters.content.required)]"
                class="required ma-3"
                :label="translate($store.getters.content.last_name)"
                required
                hide-details
              ></v-text-field>

              <!-- gender -->
              <v-select
                :items="genderList"
                item-text="name"
                item-value="abbr"
                v-model="gender"
                :label="translate($store.getters.content.select_gender)"
                :rules="[(v) => !!v || translate($store.getters.content.required)]"
                persistent-hint
                return-object
                single-line
                hide-details
                class="required ma-3"
              ></v-select>

              <!-- birthday -->
              <v-dialog
                ref="dialogBirthDay"
                v-model="birthDatePatientDialogOpen"
                :close-on-content-click="false"
                transition="scale-transition"
                width="290px"
                hide-details
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    class="birthdaypicker-custom required ma-3"
                    :value="computedBirthDate"
                    :rules="[(v) => !!v || translate($store.getters.content.required)]"
                    append-icon="mdi-calendar"
                    clearable
                    :label="translate($store.getters.content.birth_date)"
                    readonly
                    v-on="on"
                    hide-details
                  ></v-text-field>
                </template>
                <v-date-picker v-model="dateOfBirth" ref="picker" min="1900-01-01" @change="saveBirthDay($event)"></v-date-picker>
              </v-dialog>

              <!-- email  -->
              <v-text-field
                v-model="formData.email"
                :label="translate($store.getters.content.email)"
                :rules="emailRules"
                type="email"
                hint="john.doe@mail.com"
                class="required ma-3"
                hide-details
              ></v-text-field>

              <!-- phone /address  -->
              <v-text-field
                v-model="formData.phoneNumber"
                :label="translate($store.getters.content.phone)"
                type="tel"
                :rules="[(v) => !!v || translate($store.getters.content.required)]"
                hint="593 1234"
                class="required ma-3"
                hide-details
              ></v-text-field>

              <v-autocomplete
                :items="addressList"
                :label="translate($store.getters.content.address_street)"
                @change="updateAddressList()"
                hide-no-data
                :rules="[(v) => !!v || translate($store.getters.content.required)]"
                class="required ma-3"
                hide-details
                v-model="address"
              ></v-autocomplete>

              <v-text-field
                v-model="addressNr"
                :label="translate($store.getters.content.address_street_nr)"
                hint="eg. 100A"
                :rules="[(v) => !!v || translate($store.getters.content.required)]"
                class="required ma-3"
                hide-details
              ></v-text-field>

              <!-- azv / doctor  -->
              <v-text-field
                v-model="formData.azvNumber"
                :label="translate($store.getters.content.azv_number)"
                type="number"
                hint="1071234567"
                :rules="[(v) => !!v || translate($store.getters.content.required)]"
                class="required ma-3"
                hide-details
              ></v-text-field>
              <v-autocomplete
                clearable
                :disabled="doctorOptions.length == 0"
                :loading="doctorOptions.length == 0"
                v-model="formData.doctorEntry.id"
                data-lpignore="true"
                :items="doctorOptions"
                :label="translate($store.getters.content.doctor)"
                :rules="[(v) => !!v || translate($store.getters.content.required)]"
                class="required ma-3"
                hide-details
              ></v-autocomplete>

              <div class="submit-btn mt-5 mb-5 pb-5 pt-5">
                <v-btn class="mr-4 float-right" large color="primary" type="submit">{{ translate($store.getters.content.create_user) }}</v-btn>
              </div>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from 'moment';
import SharedFunctions from '@/mixins/SharedFunctions.vue';
import Shared from '@/helpers/Shared.vue';

export default {
  props: ['formError'],
  mixins: [SharedFunctions],
  data() {
    return {
      formValid: null,
      formData: {
        firstName: null,
        lastName: null,
        gender: null,
        email: null,
        phoneNumber: null,
        dateOfBirth: null,
        accommodationAddress: null,
        travelArrival: null, //registration date
        azvNumber: null,
        doctorEntry: {
          id: null,
        },
      },
      emptyItems: false,
      emailRules: [
        (v) => !!v || this.$store.getters.content.required[this.$store.state.config.selectedLanguage],
        (v) => /.+@.+\..+/.test(v) || this.$store.getters.content.email_must_be_valid[this.$store.state.config.selectedLanguage],
      ],
      genderList: [
        {
          name: this.$store.getters.content.female[this.$store.state.config.selectedLanguage],
          abbr: this.$store.getters.content.female[this.$store.state.config.submitFormLanguage],
        },
        {
          name: this.$store.getters.content.male[this.$store.state.config.selectedLanguage],
          abbr: this.$store.getters.content.male[this.$store.state.config.submitFormLanguage],
        },
      ],
      languageTag: 'en-US',
      birthDatePatientDialogOpen: false,
      dateOfBirth: '',
      gender: null,
      address: '',
      addressNr: '',
    };
  },
  watch: {
    birthDatePatientDialogOpen(val) {
      val && setTimeout(() => (this.$refs.picker.activePicker = 'YEAR'));
    },
  },
  computed: {
    doctorOptions() {
      return this.$store.state.intakeFormData.doctorOptions;
    },
    computedBirthDate() {
      return this.formData.dateOfBirth ? moment(this.formData.dateOfBirth, 'YYYY-MM-DD').format('D-MMMM-YYYY') : '';
    },
    addressList: {
      get() {
        return this.$store.state.createUserPage.addresses;
      },
      set(value) {
        this.$store.commit('SET_ADDRESS_LIST', value);
      },
    },
  },
  created() {
    this.formData.travelArrival = moment().format('YYYY-MM-DD') + 'T01:00:00.000Z[UTC]';
    if (this.doctorOptions.length == 0) this.$store.dispatch('GET_DOCTOR_OPTIONS');
    //if (this.addressList.length == 0) this.getAddressList();
    this.getAddressList();
    this.calendarLang();
  },
  beforeDestroy() {
    this.address = null;
  },
  methods: {
    updateValue(value, keyName) {
      if (this.formData[keyName] === undefined) {
        this.$set(this.formData, keyName, null);
      }
      this.formData[keyName] = value;
    },
    saveDate() {
      this.birthDateDialogOpen = false;
    },
    createUser() {
      if (!this.$refs.createUserForm.validate()) return;
      this.formData.gender = this.gender.abbr;
      this.formData.accommodationAddress = this.address + ' ' + this.addressNr;

      this.$store.dispatch('CREATE_CASE', this.formData).then((data) => {
        if (data) {
          this.$router.push({ name: 'Home' });
        }
      });
    },
    calendarLang() {
      const languageTag = Shared.languageTag();
      const locale = Shared.locale();

      if (languageTag != '') {
        this.languageTag = languageTag;
      }

      if (locale != '') {
        moment.locale(locale);
      }
    },
    saveBirthDay(value) {
      this.formData.dateOfBirth = value + 'T13:00:00.000Z[UTC]';
      this.birthDatePatientDialogOpen = false;
    },

    getAddressList() {
      this.$store.dispatch('GET_ADDRESS_LIST').then((data) => {
        if (data.length > 0) {
          this.addressList = data;
        }
      });
    },

    updateAddressList() {
      this.$store.dispatch('GET_ADDRESS_LIST', this.address).then((data) => {
        if (data) {
          this.addressList = data;
        }
      });
    },

    checkAddress(address) {
      const addressList = this.$store.dispatch('GET_ADDRESS_LIST', address);
      if (addressList.length == 0) {
        return false;
      } else {
        for (const item in addressList) {
          if (addressList[item] == address.toUpperCase()) {
            return true;
          }
        }
      }
      return false;
    },
  },
};
</script>

<style lang="scss">
.submit-btn {
  display: block;
}
</style>
